// =====================================================
// VISOR DE IMÁGENES SATELITALES
// src/pages/Agro/Productor/Establecimientos/VisorImagenesSatelitales.js
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  ButtonGroup,
  Spinner,
  Badge,
  Input,
  Label,
  FormGroup,
  Alert,
} from "reactstrap"
import { format, subDays, subMonths } from "date-fns"
import { es } from "date-fns/locale"

// Importar funciones de API
import { getImagenPoligono, getSerieImagenes } from "api/agro"

const VisorImagenesSatelitales = ({ idEstablecimiento, poligonos = [] }) => {
  const [poligonoSeleccionado, setPoligonoSeleccionado] = useState(null)
  const [fechaSeleccionada, setFechaSeleccionada] = useState(
    format(subDays(new Date(), 5), "yyyy-MM-dd"),
  )
  const [tipoVisualizacion, setTipoVisualizacion] = useState("TRUE-COLOR")
  const [imagenActual, setImagenActual] = useState(null)
  const [serieImagenes, setSerieImagenes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [modoComparacion, setModoComparacion] = useState(false)

  // Seleccionar primer polígono por defecto
  useEffect(() => {
    if (poligonos.length > 0 && !poligonoSeleccionado) {
      setPoligonoSeleccionado(poligonos[0].id)
    }
  }, [poligonos])

  // Cargar imagen cuando cambia selección
  useEffect(() => {
    if (poligonoSeleccionado && fechaSeleccionada) {
      cargarImagen()
    }
  }, [poligonoSeleccionado, fechaSeleccionada])

  const cargarImagen = async () => {
    if (!poligonoSeleccionado) return

    setLoading(true)
    setError(null)

    try {
      const data = await getImagenPoligono(
        idEstablecimiento,
        poligonoSeleccionado,
        fechaSeleccionada,
      )
      setImagenActual(data)
    } catch (err) {
      setError("Error cargando imagen satelital")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const cargarSerie = async () => {
    if (!poligonoSeleccionado) return

    setLoading(true)
    setError(null)

    try {
      const hasta = format(new Date(), "yyyy-MM-dd")
      const desde = format(subMonths(new Date(), 3), "yyyy-MM-dd")

      const data = await getSerieImagenes(
        idEstablecimiento,
        poligonoSeleccionado,
        desde,
        hasta,
        tipoVisualizacion,
        15, // cada 15 días
      )
      setSerieImagenes(data)
      setModoComparacion(true)
    } catch (err) {
      setError("Error cargando serie de imágenes")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const getUrlActual = () => {
    if (!imagenActual) return null

    switch (tipoVisualizacion) {
      case "NDVI":
        return imagenActual.urlNdvi
      case "MOISTURE":
        return imagenActual.urlMoisture
      default:
        return imagenActual.urlTrueColor
    }
  }

  const getNombrePoligono = id => {
    const pol = poligonos.find(p => p.id === id)
    return pol?.nombreLote || pol?.nombre || `Lote ${id}`
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">
            <i className="mdi mdi-satellite-variant me-2" />
            Imágenes Satelitales
          </h5>
          <div>
            <Badge color="info">Sentinel-2</Badge>
          </div>
        </div>
        {/* Controles */}
        <Row className="mb-3">
          <Col md={3}>
            <FormGroup>
              <Label size="sm">Lote/Polígono</Label>
              <Input
                type="select"
                bsSize="sm"
                value={poligonoSeleccionado || ""}
                onChange={e => setPoligonoSeleccionado(Number(e.target.value))}
              >
                <option value="">Seleccionar...</option>
                {poligonos.map(pol => (
                  <option key={pol.id} value={pol.id}>
                    {pol.nombreLote || pol.nombre || `Lote ${pol.id}`}
                    {pol.superficieHa ? ` (${pol.superficieHa} ha)` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label size="sm">Fecha</Label>
              <Input
                type="date"
                bsSize="sm"
                value={fechaSeleccionada}
                onChange={e => setFechaSeleccionada(e.target.value)}
                max={format(new Date(), "yyyy-MM-dd")}
              />
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label size="sm">Visualización</Label>
              <ButtonGroup size="sm" className="d-block">
                <Button
                  color={
                    tipoVisualizacion === "TRUE-COLOR"
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() => setTipoVisualizacion("TRUE-COLOR")}
                >
                  <i className="mdi mdi-image me-1" />
                  Color Real
                </Button>
                <Button
                  color={
                    tipoVisualizacion === "NDVI" ? "success" : "outline-success"
                  }
                  onClick={() => setTipoVisualizacion("NDVI")}
                >
                  <i className="mdi mdi-leaf me-1" />
                  NDVI
                </Button>
                <Button
                  color={
                    tipoVisualizacion === "MOISTURE" ? "info" : "outline-info"
                  }
                  onClick={() => setTipoVisualizacion("MOISTURE")}
                >
                  <i className="mdi mdi-water me-1" />
                  Humedad
                </Button>
              </ButtonGroup>
            </FormGroup>
          </Col>

          <Col md={2} className="d-flex align-items-end">
            <Button
              color="secondary"
              size="sm"
              onClick={cargarSerie}
              disabled={!poligonoSeleccionado || loading}
            >
              <i className="mdi mdi-timeline me-1" />
              Comparar fechas
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert color="danger" className="mb-3">
            <i className="mdi mdi-alert-circle me-2" />
            {error}
          </Alert>
        )}

        {/* Imagen principal */}
        {!modoComparacion ? (
          <div
            className="text-center position-relative"
            style={{ minHeight: 400 }}
          >
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: 400 }}
              >
                <Spinner color="primary" />
                <span className="ms-2">Cargando imagen satelital...</span>
              </div>
            ) : getUrlActual() ? (
              <>
                <img
                  src={getUrlActual()}
                  alt={`Imagen satelital - ${tipoVisualizacion}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 500,
                    border: "2px solid #dee2e6",
                    borderRadius: 8,
                  }}
                  onError={e => {
                    e.target.style.display = "none"
                    setError(
                      "No se pudo cargar la imagen. Puede que no haya datos disponibles para esta fecha.",
                    )
                  }}
                />
                <div className="mt-2 text-muted">
                  <small>
                    <i className="mdi mdi-map-marker me-1" />
                    {getNombrePoligono(poligonoSeleccionado)} |
                    <i className="mdi mdi-calendar ms-2 me-1" />
                    {format(new Date(fechaSeleccionada), "dd/MM/yyyy", {
                      locale: es,
                    })}
                  </small>
                </div>
              </>
            ) : (
              <div
                className="text-muted d-flex flex-column justify-content-center align-items-center"
                style={{ height: 400 }}
              >
                <i
                  className="mdi mdi-satellite-variant"
                  style={{ fontSize: 64, opacity: 0.3 }}
                />
                <p className="mt-3">
                  Seleccioná un lote y fecha para ver la imagen satelital
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Modo comparación - Serie temporal */
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="mdi mdi-timeline me-2" />
                Evolución temporal - Últimos 3 meses
              </h6>
              <Button
                color="link"
                size="sm"
                onClick={() => setModoComparacion(false)}
              >
                <i className="mdi mdi-close me-1" />
                Cerrar comparación
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Cargando serie de imágenes...</p>
              </div>
            ) : (
              <Row>
                {serieImagenes.map((img, idx) => (
                  <Col key={idx} md={3} sm={6} className="mb-3">
                    <Card className="h-100">
                      <img
                        src={img.urlImagen || img.urlThumbnail}
                        alt={`Imagen ${img.fecha}`}
                        style={{
                          width: "100%",
                          height: 150,
                          objectFit: "cover",
                          borderTopLeftRadius: 4,
                          borderTopRightRadius: 4,
                        }}
                        onError={e => {
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect fill='%23f0f0f0' width='200' height='150'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3ESin datos%3C/text%3E%3C/svg%3E"
                        }}
                      />
                      <CardBody className="p-2 text-center">
                        <small className="text-muted">
                          {format(new Date(img.fecha), "dd MMM yyyy", {
                            locale: es,
                          })}
                        </small>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        )}

        {/* Leyenda NDVI */}
        {tipoVisualizacion === "NDVI" && !modoComparacion && (
          <div
            className="mt-3 p-2 bg-light rounded font-size-14"
            data-testid="leyenda-ndvi"
          >
            <span className="text-muted d-block mb-1">Leyenda NDVI:</span>
            <div className="d-flex flex-wrap justify-content-between gap-2">
              <span>
                <span style={{ color: "#d73027" }}>■■■■</span> Suelo
                desnudo/Estrés
              </span>
              <span>
                <span style={{ color: "#fc8d59" }}>■■■■</span> Vegetación escasa
              </span>
              <span>
                <span style={{ color: "#fee08b" }}>■■■■</span> Vegetación
                moderada
              </span>
              <span>
                <span style={{ color: "#91cf60" }}>■■■■</span> Vegetación densa
              </span>
              <span>
                <span style={{ color: "#1a9850" }}>■■■■</span> Vegetación muy
                densa
              </span>
            </div>
          </div>
        )}

        {/* Leyenda MOISTURE */}
        {tipoVisualizacion === "MOISTURE" && !modoComparacion && (
          <div
            className="mt-3 p-2 bg-light rounded font-size-14"
            data-testid="leyenda-humedad"
          >
            <span className="text-muted d-block mb-1">Leyenda Humedad:</span>
            <div className="d-flex flex-wrap justify-content-between gap-2">
              <span>
                <span style={{ color: "#800000" }}>■■■■</span> Muy seco - Suelo
                desnudo, vegetación muerta o cosechada
              </span>
              <span>
                <span style={{ color: "#ff0000" }}>■■■■</span> Seco - Estrés
                hídrico severo, vegetación muy estresada
              </span>
              <span>
                <span style={{ color: "#ffff00" }}>■■■■</span> Moderado -
                Humedad baja, posible estrés inicial
              </span>
              <span>
                <span style={{ color: "#00ffff" }}>■■■■</span> Húmedo - Buena
                disponibilidad de agua en la vegetación
              </span>
              <span>
                <span style={{ color: "#0000ff" }}>■■■■</span> Muy húmedo - Alta
                humedad, vegetación vigorosa
              </span>
              <span>
                <span style={{ color: "#000080" }}>■■■■</span> Saturado -
                Posible anegamiento o cuerpos de agua
              </span>
            </div>
          </div>
        )}

        {/* Info sobre el servicio */}
        <div
          className="mt-3 text-muted font-size-14"
          data-testid="fuente-satelital"
        >
          <i className="mdi mdi-information-outline me-1" />
          Imágenes satelitales provistas por Sentinel-2 (ESA/Copernicus).
          Resolución: 10m/pixel. Actualización: cada 5 días (sin nubes).
        </div>
      </CardBody>
    </Card>
  )
}

export default VisorImagenesSatelitales
